import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { FieldValues } from 'react-hook-form';
import { ICandidate, IJob, indexMapping, IRelocationPreference } from 'wisestep-api';
import { willingToTravelList, relocationList, authorizationCategoryList } from 'wisestep-base';

import IOtherDocument from '../types/IOtherDocument';

const getNoticePeriodInDays = (day: { index: number; key: string } | null) => {
  let correctDay = -1;
  if (day) {
    switch (day.key) {
      case 'Immediately':
        correctDay = 0;
        break;
      case '30 Days':
        correctDay = 30;
        break;
      case '45 Days':
        correctDay = 45;
        break;
      case '60 Days':
        correctDay = 60;
        break;
      case '90 Days':
        correctDay = 90;
        break;
    }
  }
  return day && correctDay != -1 ? correctDay : null;
};

export const getRelocationPreference = (
  relocationPreference: number | { index: number; key: string },
  isRelocation: boolean | null
) => {
  if (!isRelocation) {
    return IRelocationPreference.NO_RELOCATION;
  }
  let relocation;
  const isRelocationPreferenceNumber = typeof relocationPreference == 'number' ? true : false;
  if (isRelocationPreferenceNumber) {
    relocation = Object.keys(relocationList).find(
      (key) => relocationList[key] === relocationPreference
    );
  } else if (typeof relocationPreference == 'object' && relocationPreference) {
    switch (relocationPreference?.key) {
      case IRelocationPreference.WITHIN_STATE:
        relocation = IRelocationPreference.WITHIN_STATE;
        break;
      case IRelocationPreference.WITHIN_COUNTRY:
        relocation = IRelocationPreference.WITHIN_COUNTRY;
        break;
      case IRelocationPreference.ANYWHERE:
        relocation = IRelocationPreference.ANYWHERE;
        break;
      default:
        relocation = IRelocationPreference.NONE;
        break;
    }
  }
  return relocation;
};

export const settingWillingToTravelType = (
  willingToTravelType: number | { index: number; key: string }
) => {
  const isWillingToTravelTypeNumber = typeof willingToTravelType == 'number' ? true : false;
  if (isWillingToTravelTypeNumber) {
    const value = Object.keys(willingToTravelList).find(
      (key) => willingToTravelList[key] === willingToTravelType
    );
    return value == 'Yes' ? true : false;
  } else if (typeof willingToTravelType == 'object' && willingToTravelType?.key) {
    return willingToTravelType?.key ? (willingToTravelType.key === 'Yes' ? true : false) : null;
  }
  return null;
};

const getAuthorizationCategoryType = (
  authorizationCategory: number | { index: number; key: string }
) => {
  const isAuthorizationCategoryNumber = typeof authorizationCategory == 'number' ? true : false;
  if (isAuthorizationCategoryNumber) {
    return Object.keys(authorizationCategoryList).find(
      (key) => authorizationCategoryList[key] === authorizationCategory
    );
  } else if (typeof authorizationCategory == 'object' && authorizationCategory?.key) {
    return authorizationCategory?.key ? authorizationCategory.key : null;
  }
};

const setCandidateActionObj = (candidate: ICandidate, data: FieldValues) => {
  if (!isEmpty(candidate.contactActionsObjs)) {
    const contactActionsObjs = candidate.contactActionsObjs;
    contactActionsObjs?.push({
      actions: data.recruiterActivity.actions ?? [],
      notes: data.notes,
      result: {
        result: data.recruiterActivity.results ?? [],
      },
    });
    return contactActionsObjs;
  }
  return [
    {
      actions: data.recruiterActivity.actions ?? [],
      notes: data.notes,
      result: {
        result: data.recruiterActivity.results ?? [],
      },
    },
  ];
};

const setWorkAuthorization = (authorization, workAuthorization, workAuthorizationCategory) => {
  if (workAuthorizationCategory) {
    if (workAuthorizationCategory === 'Others') {
      return workAuthorization ?? null;
    } else {
      return authorization
        ? typeof authorization == 'object'
          ? authorization.key
          : authorization
        : null;
    }
  }
  return null;
};

export const saveMapping = async (
  data: FieldValues,
  candidate: ICandidate | null,
  job: IJob,
  document?: IOtherDocument[] | null,
  cand?: any
) => {
  const mappingEntity = createMappingEntity(data, candidate, job, document, cand);
  try {
    const save = await indexMapping(mappingEntity);
    return {
      status: 'success',
      response: save.data,
      responseCode: save?.status,
    };
  } catch (e) {
    const error = e as Error | AxiosError;
    if (axios.isAxiosError(error) && error) {
      return {
        status: 'fail',
        data: error?.response?.data,
        response: error?.response?.data?.details ?? null,
        responseCode: error?.response?.status,
      };
    }
    return null;
  }
};

export const createMappingEntity = (
  data: FieldValues,
  candidate: ICandidate | null,
  job: IJob,
  document?: IOtherDocument[] | null,
  cand?: any
) => {
  return {
    assessment: data?.recruiterAssessment,
    contactActionsObjs: [
      {
        actions: data.recruiterActivity?.actions ?? [],
        notes: data.notes,
        result: {
          result: data.recruiterActivity?.results ?? [],
        },
      },
    ],
    alternateEmails: data.alternateEmails ?? candidate?.alternateEmails,
    alternatePhoneNumbers: !isEmpty(data.alternatePhoneNumbers)
      ? data.alternatePhoneNumbers.map((e) => e.toString())
      : candidate?.alternatePhoneNumbers,
    applicationStage: null,
    applicationState: null,
    candidateId: candidate?.candidateId ?? candidate?.id,
    bio: candidate?.bio ?? null,
    calendarEvents: null,
    candidateInteractionResult: candidate?.candidateInteractionResult ?? null,
    candidateJobBoardId: candidate?.candidateJobBoardId ?? null,
    candidateNotes: candidate?.candidateNotes ?? null,
    candidateSkills: candidate?.candidateSkills ?? null,
    candidateTasks: candidate?.candidateTasks ?? null,
    candidateStatusTags: candidate?.candidateStatusTags ?? [],
    candidateLanguages: data.candidateLanguages ?? [],
    certifications: data.certifications,
    citizenship: data.citizenship,
    clientJobCode: candidate?.clientJobCode ?? null,
    commPreferenceEmail: data.communicationPreference?.key?.includes('Email') ? true : false,
    commPreferencePhone: data.communicationPreference?.key?.includes('Phone') ? true : false,
    jobPreferenceContract: data.jobPreference?.key?.includes('Contract') ? true : false,
    jobPreferencePermanent: data.jobPreference?.key?.includes('Permanent') ? true : false,
    currentEmployer: data.currentCompany ?? '',
    currentJobTitle: data.jobTitle ?? '',
    currentLocation: data.currentLocation ? data.currentLocation[0] : candidate?.currentLocation,
    willingToTravelType: !isEmpty(data.willingToTravelType) ? data.willingToTravelType : null,
    willingToRelocate: data?.willingToTravel?.key
      ? data.willingToTravel.key === 'Yes'
        ? true
        : false
      : null,
    currentJobType: data?.currentJobType?.key ?? null,
    jobType: null,
    jobWorkAssignment: null,
    companyName: candidate?.companyName ?? null,
    contactActions: null,
    createdBy: null,
    createdOn: null,
    createdWith: 'Wisestep',
    crmState: candidate?.crmState,
    currentSalary: data.currentSalary?.value,
    currentSalaryCurrency: data.currentSalary?.currency,
    currentSalaryTimeSpan: data.currentSalary?.timeSpan,
    customNote: candidate?.customNote,
    dateLastActiveOnJobBoard: candidate?.dateLastActiveOnJobBoard ?? null,
    dateResumeLastUpdatedOnJobBoard: candidate?.dateResumeLastUpdatedOnJobBoard ?? null,
    desiredJobTitles: data?.desiredJobTitles ?? [],
    dnc: candidate?.dnc ?? null,
    dncDetails: candidate?.dncDetails ?? null,
    dob: data?.dob ? dayjs(data?.dob).format('YYYY-MM-DDTHH:mm:ss') : null,
    downloadSource: data.downloadSource ?? null,
    education: candidate?.education ?? [],
    educationQualifications: data.highestEducation,
    email: data.email ?? candidate?.email,
    expectedBaseSalary: data.expectedSalary?.value ?? '',
    expectedBaseSalaryTimeSpan: data.expectedSalary?.timeSpan,
    expectedSalaryCurrency: data.expectedSalary?.currency,
    experience: candidate?.experience ?? null,
    familyWillingToRelocate: data.familyWillingToRelocate
      ? data.familyWillingToRelocate?.key == 'Yes'
        ? true
        : false
      : null,
    relocationPreference: getRelocationPreference(
      data.relocationList,
      settingWillingToTravelType(data?.willingToTravel)
    ),
    preferredLocations: data.preferredLocation ?? [],
    fax: candidate?.fax ?? null,
    gender: data.gender,
    hasVeteranStatus: data?.hasVeteranStatus ? data?.hasVeteranStatus?.key === 'Yes' : null,
    id: null,
    imageUrl: candidate?.imageUrl ?? null,
    noticePeriodInDays:
      getNoticePeriodInDays(data.noticePeriodInDays) ??
      (candidate?.noticePeriodInDays ? candidate?.noticePeriodInDays : null),
    noticePeriod: null,
    institutionsAttended: data.institutesAttended,
    interests: candidate?.interests ?? null,
    isApplied: candidate?.isApplied ?? null,
    isCreatedWhenCandidateMapped: true,
    isFromCareerPage: candidate?.isFromCareerPage ?? null,
    isInDnc: candidate?.isInDnc ?? null,
    isInterested: candidate?.isInterested ?? null,
    isJobCandidateMapping: true,
    jobAccountManagerEmail: null,
    jobAccountManagerName: candidate?.jobAccountManagerName ?? null,
    jobClientRecruiterEmail: null,
    jobClientRecruiterName: candidate?.jobClientRecruiterName ?? null,
    jobCode: null,
    jobId: job.id ?? null,
    jobLocations: null,
    jobMaximumSalary: null,
    jobMinimumSalary: null,
    lastCallAction: candidate?.lastCallAction ?? null,
    lastEmailAction: candidate?.lastEmailAction ?? null,
    lastSmsAction: candidate?.lastSmsAction ?? null,
    likelyToMove: candidate?.likelyToMove ?? null,
    likelyToMoveScore: candidate?.likelyToMoveScore ?? null,
    linkedCandidateContacts: candidate?.linkedCandidateContacts ?? null,
    linkedCandidateIds: candidate?.linkedCandidateIds ?? null,
    locations: candidate?.locations,
    mappedJobLocation: candidate?.mappedJobLocation ?? '',
    mappedJobTitle: candidate?.mappedJobTitle ?? '',
    messengers: data.messengerLinks ?? candidate?.messengers,
    modifiedBy: null,
    modifiedOn: null,
    mostRecentConversation: null,
    name: data.name ?? candidate?.name,
    nationality: data.nationality,
    otherCurrentAllowances: data.otherAllowance,
    otherExpectedAllowances: data.expectedAllowance,
    phoneNumber: data.phone ? data.phone.toString() : candidate?.phoneNumber,
    previousEmployers: data.prevEmployers ?? [],
    previousJobTitles: data.prevJobTitles ?? [],
    profileURLs: data.socialLinks ?? candidate?.profileURLs,
    reasonForNewJob: data.jobChangeReason ?? candidate?.reasonForNewJob,
    recruiterCompanyName: null,
    recruiterId: null,
    recruiterJobMappingId: candidate?.recruiterJobMappingId ?? '',
    recruiterName: null,
    relevantMonthsExperience: data.relExpInMonth,
    relevantYearsExperience: data.relExpInYears,
    responseData: candidate?.responseData ?? null,
    rtrConsent: candidate?.rtrConsent,
    rtrFileLocation: candidate?.rtrFileLocation ?? null,
    rtrFileOriginalName: candidate?.rtrFileOriginalName ?? null,
    rtrFileUploadedOn: candidate?.rtrFileUploadedOn ?? null,
    scoringData: candidate?.scoringData ?? null,
    searchId: candidate?.searchId ?? null,
    securityAllowance: data?.securityClearance
      ? {
          value: null,
          status: data?.securityClearance ? data?.securityClearance?.key === 'Yes' : null,
          issuingAgency: null,
          polygraph: null,
          notes: null,
        }
      : candidate?.securityAllowance ?? null,
    sendMessage: candidate?.sendMessage,
    showContact: candidate?.showContact,
    skills: data.skills,
    slug: candidate?.slug,
    socialProfiles: candidate?.socialProfiles ?? null,
    socialUrls: candidate?.socialUrls,
    tags: data.tags ?? [],
    source: data.source,
    stageAndStateActions: null,
    stageDate: null,
    submittedOn: null,
    team: candidate?.team ?? null,
    timeToPost: null,
    totalMonthsExperience: data.expInMonth,
    totalYearsExperience: data.expInYears,
    turnAroundTime: candidate?.turnAroundTime ?? null,
    workAuthorization: setWorkAuthorization(
      data.authorization,
      data?.workAuthorization,
      getAuthorizationCategoryType(data.authorizationCategory)
    ),
    workAuthorizationCategory: getAuthorizationCategoryType(data.authorizationCategory) ?? null,
    visaStatus: data.visaStatus,
    visaValidity: data.visaValidity,
    workPermitDocuments: null,
    workPermitLocations: null,
    workPermits: null,
    workPreferences: null,
    workspaceId: null,
    workspaceName: null,
    mappedCandidateResumes: candidate?.mappedCandidateResumes ?? null,
    resume: candidate?.mappedCandidateResumes ? candidate?.mappedCandidateResumes[0] : null,
    resumeLocation: candidate?.resumeLocation ?? null,
    resumeFileOriginalName: candidate?.resumeFileOriginalName ?? '',
    resumeUploadedOn: candidate?.resumeUploadedOn,
    otherDocuments: document ? document : candidate?.otherDocuments,
    jobPostedByRecruiterName: null,
    jobPostingDate: null,
    isSuitable: null,
    jobPostedByTeam: null,
    isMapped: null,
    isSubmitted: null,
    isUploaded: null,
    passportType: data?.passportType,
    passportNumber: data?.passportNumber,
    age: data?.age,
    postalAddress: data?.postalAddress,
    ssnNumber: data?.ssnNumber,
    candidatePersonalId: data?.candidatePersonalId,
    panCard: data?.panCard,
    aadharNumber: data?.aadharNumber,
    availableFrom: data?.availableFrom,
    interviewingWithOthers: data?.interviewingWithOthers
      ? data.interviewingWithOthers.key === 'Yes'
      : undefined,
    jobIncludesTravel: data?.currentJobIncludesTravel
      ? data.currentJobIncludesTravel.key === 'Yes'
      : undefined,
    currentJobTravelType: data?.currentJobTravelType,
    personalWebsite: data?.personalWebsite ?? '',
    feeArrangementId: job?.feeArrangementId,
    parseCV: true,
    candidateMaster: cand ?? null,
  };
};

export const updateMapping = async (data: FieldValues, candidate: ICandidate) => {
  const mappingEntity = {
    ...candidate,
    assessment: data?.recruiterAssessment,
    contactActionsObjectsArray: [
      {
        actions: data.recruiterActivity.actions ?? [],
        notes: data.notes,
        result: {
          result: data.recruiterActivity.results ?? [],
        },
      },
    ],
    contactActionsObjs: setCandidateActionObj(candidate, data),
    alternateEmails: data.alternateEmails ?? candidate.alternateEmails,
    alternatePhoneNumbers: !isEmpty(data.alternatePhoneNumbers)
      ? data.alternatePhoneNumbers.map((e) => e.toString())
      : candidate.alternatePhoneNumbers ?? [],
    candidateId: data.candidateId ?? candidate.candidateId,
    candidateLanguages: data.candidateLanguages ?? [],
    certifications: data.certifications,
    citizenship: data.citizenship,
    commPreferenceEmail: data.communicationPreference?.key?.includes('Email') ? true : false,
    commPreferencePhone: data.communicationPreference?.key?.includes('Phone') ? true : false,
    jobPreferenceContract: data.jobPreference?.key?.includes('Contract') ? true : false,
    jobPreferencePermanent: data.jobPreference?.key?.includes('Permanent') ? true : false,
    currentEmployer: data.currentCompany,
    currentJobTitle: data.jobTitle,
    currentLocation: data.currentLocation ? data.currentLocation[0] : candidate.currentLocation,
    willingToRelocate: settingWillingToTravelType(data?.willingToTravel),
    currentJobType: data?.currentJobType?.key ?? null,
    createdWith: 'Wisestep',
    downloadSource: data.downloadSource ?? null,
    currentSalary: data.currentSalary?.value ?? null,
    currentSalaryCurrency: data.currentSalary?.currency ?? null,
    currentSalaryTimeSpan: data.currentSalary?.timeSpan ?? null,
    educationQualifications: data.highestEducation,
    email: data.email ?? candidate.email,
    expectedBaseSalary: data.expectedSalary?.value ?? null,
    expectedBaseSalaryTimeSpan: data.expectedSalary?.timeSpan ?? null,
    expectedSalaryCurrency: data.expectedSalary?.currency ?? null,
    familyWillingToRelocate: data.familyWillingToRelocate
      ? data.familyWillingToRelocate.key == 'Yes'
        ? true
        : false
      : null,
    relocationPreference: getRelocationPreference(
      data.relocationList,
      settingWillingToTravelType(data?.willingToTravel)
    ),
    preferredLocations: settingWillingToTravelType(data?.willingToTravel)
      ? data.preferredLocation
      : [],
    noticePeriodInDays:
      getNoticePeriodInDays(data.noticePeriodInDays) ?? candidate.noticePeriodInDays,
    institutionsAttended: data.institutesAttended,
    messengers: data.messengerLinks ?? candidate.messengers,
    name: data.name ?? candidate.name,
    nationality: data.nationality,
    otherCurrentAllowances: data.otherAllowance,
    otherExpectedAllowances: data.expectedAllowance,
    phoneNumber: data.phone ? data.phone.toString() : candidate?.phoneNumber ?? '',
    previousEmployers: data.prevEmployers ?? [],
    previousJobTitles: data.prevJobTitles ?? [],
    desiredJobTitles: data?.desiredJobTitles ?? [],
    profileURLs: data.socialLinks ?? candidate.profileURLs,
    reasonForNewJob: data.jobChangeReason ?? candidate.reasonForNewJob,
    relevantMonthsExperience: data.relExpInMonth,
    relevantYearsExperience: data.relExpInYears,
    isCreatedWhenCandidateMapped: true,
    skills: data.skills,
    tags: data.tags ?? [],
    source: data.source,
    totalMonthsExperience: data.expInMonth,
    totalYearsExperience: data.expInYears,
    workAuthorization: setWorkAuthorization(
      data.authorization,
      data?.workAuthorization,
      getAuthorizationCategoryType(data.authorizationCategory)
    ),
    workAuthorizationCategory: getAuthorizationCategoryType(data.authorizationCategory) ?? null,
    visaStatus: data.visaStatus,
    visaValidity: data.visaValidity,
    passportType: data?.passportType,
    passportNumber: data?.passportNumber,
    age: data?.age,
    dob: data?.dob ? dayjs(data?.dob).format('YYYY-MM-DDTHH:mm:ss') : null,
    gender: data?.gender,
    postalAddress: data?.postalAddress,
    ssnNumber: data?.ssnNumber,
    panCard: data?.panCard,
    aadharNumber: data?.aadharNumber,
    candidatePersonalId: data?.candidatePersonalId,
    availableFrom: data?.availableFrom,
    parseCV: true,
    interviewingWithOthers: data?.interviewingWithOthers
      ? data.interviewingWithOthers.key === 'Yes'
      : undefined,
    jobIncludesTravel: data?.currentJobIncludesTravel
      ? data.interviewingWithOthers.key === 'Yes'
      : undefined,
    currentJobTravelType: data?.currentJobTravelType,
    hasVeteranStatus: data?.hasVeteranStatus ? data?.hasVeteranStatus.key === 'Yes' : null,
    personalWebsite: data?.personalWebsite ?? '',
    securityAllowance: data?.securityClearance
      ? {
          value: null,
          status: data?.securityClearance ? data?.securityClearance.key === 'Yes' : null,
          issuingAgency: null,
          polygraph: null,
          notes: null,
        }
      : candidate?.securityAllowance ?? null,
    willingToTravelType: !isEmpty(data.willingToTravelType) ? data.willingToTravelType : null,
  };
  try {
    const update = await indexMapping(mappingEntity);
    return update.data;
  } catch (e) {
    const error = e as Error | AxiosError;
    if (axios.isAxiosError(error) && error) {
      return {
        status: 'fail',
        data: error?.response?.data,
        response: error?.response?.data?.details ?? null,
        responseCode: error?.response?.status,
      };
    }
    return null;
  }
};

export const getMappingFailedNotification = (response) => {
  let message = '';
  const lowerCaseResponse = response.toLowerCase();

  switch (true) {
    case lowerCaseResponse.includes('no accepted work assignment'):
      message = 'This Job is not Accepted by you. Please accept the job then map the candidate.';
      break;

    case lowerCaseResponse.includes('no active work assignment') ||
      lowerCaseResponse.includes('no work assignment'):
      message = 'This Job is not Assigned to you . Please contact the Job Admin team';
      break;

    default:
      message = 'Mapping failed.';
      break;
  }

  return message;
};
